import { useEffect, useState } from "react";
import { RefreshCw, LogIn } from "lucide-react";
import BrandLogo from "./shared/BrandLogo";

export default function SplashScreen({ onFinish, onEscape, ready = true }) {
  // Read the class set by the inline theme script — synchronous, no FOUC.
  // Using a plain variable (not state) because theme never changes during splash.
  const isDark = document.documentElement.classList.contains("dark");

  const [phase, setPhase]     = useState("in");   // "in" | "out"
  const [visible, setVisible] = useState(true);
  const [minDone, setMinDone] = useState(false);

  // Progressive feedback phases — trigger regardless of `ready`.
  const [waiting, setWaiting]  = useState(false); // 4.5s: subtle status message
  const [stalled, setStalled]  = useState(false); // 8.5s: retry + escape (safety net)

  // Minimum display time — ensures users always see the splash.
  useEffect(() => {
    const t = setTimeout(() => setMinDone(true), 900);
    return () => clearTimeout(t);
  }, []);

  // Progressive feedback timers.
  useEffect(() => {
    if (ready) return;
    const t1 = setTimeout(() => setWaiting(true),  4500);
    const t2 = setTimeout(() => setStalled(true),  8500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start fade-out once both minDone AND ready are true.
  useEffect(() => {
    const canFade = (minDone || stalled) && ready;
    if (!canFade) return;
    setPhase("out");
    const t = setTimeout(() => {
      setVisible(false);
      onFinish?.();
    }, 480);
    return () => clearTimeout(t);
  }, [minDone, stalled, ready, onFinish]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-start select-none transition-opacity ease-out ${
        phase === "out" ? "opacity-0" : "opacity-100"
      }`}
      style={{
        background: isDark
          ? "linear-gradient(160deg, #050d1e 0%, #0b1d43 45%, #091630 100%)"
          : "#f8fafc",
        transitionDuration: "480ms",
      }}
    >
      {/* ── Ambient glow field — dark mode only ────────────────────────────── */}
      {isDark && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[58%] h-[380px] w-[380px] rounded-full blur-[100px] animate-ambient-1"
            style={{ background: "radial-gradient(circle, rgba(27,79,156,0.35) 0%, transparent 70%)" }}
          />
          <div
            className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[240px] w-[240px] rounded-full blur-[80px] animate-ambient-2"
            style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)" }}
          />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent" />
        </div>
      )}

      {/* ── Main content ───────────────────────────────────────────────────── */}
      <div className="relative flex flex-col items-center animate-splash-in mt-[calc(50dvh-min(15vw,60px))]">

        {/* Logo + halo */}
        <div className="relative mb-8">
          <div
            className="absolute inset-0 rounded-full animate-logo-halo pointer-events-none"
            style={{
              background: isDark
                ? "radial-gradient(circle, rgba(58,123,213,0.45) 0%, transparent 65%)"
                : "radial-gradient(circle, rgba(27,79,156,0.12) 0%, transparent 65%)",
            }}
          />
          <BrandLogo
            variant={isDark ? "gradient" : "dark"}
            className="relative w-[min(30vw,120px)] h-[min(30vw,120px)] animate-breathe"
            alt="Kéves"
          />
        </div>

        {/* App name */}
        <p className={`text-[20px] font-semibold tracking-[0.05em] font-brand leading-none mb-1.5 ${
          isDark ? "text-white/90" : "text-[#1b4f9c]"
        }`}>
          Kéves App
        </p>
        <p className={`text-[11px] text-center leading-relaxed max-w-[240px] mb-10 ${
          isDark ? "text-white/35" : "text-slate-400"
        }`}>
          El SEÑOR es mi pastor; nada me faltará. — Salmos 23:1
        </p>

        {/* Shimmer loading bar */}
        <div className={`relative w-[96px] h-[2px] rounded-full overflow-hidden mb-5 ${
          isDark ? "bg-white/[0.08]" : "bg-[#1b4f9c]/10"
        }`}>
          <div className={`absolute inset-y-0 rounded-full bg-gradient-to-r animate-shimmer-splash ${
            isDark
              ? "from-transparent via-white/55 to-transparent"
              : "from-transparent via-[#1b4f9c]/40 to-transparent"
          }`} />
        </div>

        {/* ── Progressive status messages ─────────────────────────────────── */}

        {/* Phase 2 — waiting (4.5s+) */}
        {waiting && !stalled && (
          <p className={`text-[11px] tracking-[0.06em] animate-in fade-in duration-700 ${
            isDark ? "text-white/35" : "text-slate-400"
          }`}>
            Restaurando tu sesión…
          </p>
        )}

        {/* Phase 3 — stalled (8.5s+) */}
        {stalled && !ready && (
          <div className="flex flex-col items-center gap-3 animate-in fade-in duration-300 px-8">
            <p className={`text-[12px] text-center leading-relaxed ${
              isDark ? "text-white/50" : "text-slate-500"
            }`}>
              Está tomando más tiempo de lo esperado
            </p>
            <div className="flex flex-col sm:flex-row gap-2 w-full max-w-[240px]">
              <button
                onClick={() => window.location.reload()}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-[14px] px-4 py-2.5 text-[12px] font-semibold transition-all duration-200 active:scale-[0.97] ${
                  isDark
                    ? "bg-white/10 hover:bg-white/[0.15] text-white/80"
                    : "bg-[#1b4f9c]/10 hover:bg-[#1b4f9c]/20 text-[#1b4f9c]"
                }`}
              >
                <RefreshCw size={12} />
                Reintentar
              </button>
              <button
                onClick={() => onEscape ? onEscape() : (window.location.href = '/login')}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-[14px] px-4 py-2.5 text-[12px] font-semibold transition-all duration-200 active:scale-[0.97] ${
                  isDark
                    ? "bg-white/10 hover:bg-white/[0.15] text-white/80"
                    : "bg-[#1b4f9c]/10 hover:bg-[#1b4f9c]/20 text-[#1b4f9c]"
                }`}
              >
                <LogIn size={12} />
                Ir al inicio
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
